import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import { UserCog, Pencil, Check, X, ChevronDown, ChevronRight } from 'lucide-react';
import userService, { type UserData, AUSTRALIAN_STATES } from '../../services/user';
import AdminEmailEditor from './AdminEmailEditor';
import orgPolicyService, { type OvertimeRules, type UserOvertimeOverride } from '../../services/orgPolicyService';

type FormState = {
  first_name: string;
  last_name: string;
  myob_card_id: string;
  xero_employee_code: string;
  pay_frequency: string;
  state: string;
};

const FREQ_LABEL: Record<string, string> = {
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
};

const OT_DEFAULTS: OvertimeRules = {
  overtime_start_hour:   18,
  overtime_end_hour:     6,
  overtime_multiplier:   1.5,
  overtime_til_mode:     false,
  saturday_all_overtime: true,
  saturday_multiplier:   1.5,
  saturday_til_mode:     false,
  sunday_all_overtime:   true,
  sunday_multiplier:     2.0,
  sunday_til_mode:       false,
};

// ── Overtime override panel (per-user) ───────────────────────────────────────

function UserOvertimePanel({ userId }: { userId: number }) {
  const [override, setOverride] = useState<UserOvertimeOverride | null | undefined>(undefined);
  const [draft, setDraft] = useState<OvertimeRules>({ ...OT_DEFAULTS });
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!expanded || override !== undefined) return;
    orgPolicyService.getUserOvertimeOverride(userId).then(res => {
      if (res.success) {
        setOverride(res.data ?? null);
        if (res.data) setDraft(res.data);
      }
    });
  }, [expanded, userId, override]);

  const handleEnable = () => {
    setDraft({ ...OT_DEFAULTS });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const res = await orgPolicyService.upsertUserOvertimeOverride(userId, draft);
      if (res.success && res.data) {
        setOverride(res.data);
        setDraft(res.data);
      } else {
        setError('Failed to save');
      }
    } catch {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    try {
      setSaving(true);
      setError(null);
      await orgPolicyService.deleteUserOvertimeOverride(userId);
      setOverride(null);
      setDraft({ ...OT_DEFAULTS });
    } catch {
      setError('Failed to clear');
    } finally {
      setSaving(false);
    }
  };

  const isCustomised = override !== null && override !== undefined;
  const isLoading = override === undefined && expanded;

  return (
    <div className="mt-3 border border-neutral-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-3 py-2 bg-neutral-50 text-left hover:bg-neutral-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-neutral-600">Overtime Override</span>
          {isCustomised && (
            <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-medium">Custom</span>
          )}
        </div>
        {expanded
          ? <ChevronDown className="h-4 w-4 text-neutral-400" />
          : <ChevronRight className="h-4 w-4 text-neutral-400" />
        }
      </button>

      {expanded && (
        <div className="p-3 space-y-3">
          {isLoading ? (
            <p className="text-xs text-neutral-500">Loading…</p>
          ) : !isCustomised ? (
            <div className="flex items-start gap-3">
              <p className="flex-1 text-xs text-neutral-500">
                Using pay-group / org default rules.
                Set a custom override to apply different overtime rules to this individual.
              </p>
              <Button size="sm" variant="outline" onClick={handleEnable} className="text-xs shrink-0">
                Set override
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-xs text-orange-600 font-medium bg-orange-50 px-2 py-0.5 rounded">
                  Individual override active
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleClear}
                  disabled={saving}
                  className="text-xs text-red-600 border-red-200 hover:bg-red-50"
                >
                  Remove override
                </Button>
              </div>
              {error && <p className="text-xs text-red-600">{error}</p>}
              <div className="space-y-2">
                {/* Weekday */}
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Weekday</p>
                <div className="grid grid-cols-2 gap-2">
                  <OTNumberInput label="OT morning ends" value={draft.overtime_end_hour}
                    onChange={v => setDraft(p => ({ ...p, overtime_end_hour: v }))} />
                  <OTNumberInput label="OT evening starts" value={draft.overtime_start_hour}
                    onChange={v => setDraft(p => ({ ...p, overtime_start_hour: v }))} />
                </div>
                <OTNumberInput label="Weekday multiplier" value={draft.overtime_multiplier} step={0.25} min={1} max={5}
                  onChange={v => setDraft(p => ({ ...p, overtime_multiplier: v }))} suffix="x" />
                {/* Saturday */}
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide pt-1">Saturday</p>
                <OTToggle label="All Sat OT" checked={draft.saturday_all_overtime}
                  onChange={v => setDraft(p => ({ ...p, saturday_all_overtime: v }))} />
                <OTNumberInput label="Sat multiplier" value={draft.saturday_multiplier} step={0.25} min={1} max={5}
                  onChange={v => setDraft(p => ({ ...p, saturday_multiplier: v }))} suffix="x" />
                {/* Sunday */}
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide pt-1">Sunday</p>
                <OTToggle label="All Sun OT" checked={draft.sunday_all_overtime}
                  onChange={v => setDraft(p => ({ ...p, sunday_all_overtime: v }))} />
                <OTNumberInput label="Sun multiplier" value={draft.sunday_multiplier} step={0.25} min={1} max={5}
                  onChange={v => setDraft(p => ({ ...p, sunday_multiplier: v }))} suffix="x" />
              </div>
              <div className="flex justify-end">
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save override'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function OTNumberInput({ label, value, onChange, step = 0.5, min = 0, max = 24, suffix, disabled }: {
  label: string; value: number; onChange: (v: number) => void;
  step?: number; min?: number; max?: number; suffix?: string; disabled?: boolean;
}) {
  return (
    <div className={disabled ? 'opacity-40' : ''}>
      <label className="block text-xs text-neutral-500 mb-1">{label}</label>
      <div className="flex items-center gap-1">
        <input type="number" value={value} step={step} min={min} max={max}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          disabled={disabled}
          className="w-full px-2 py-1 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed"
        />
        {suffix && <span className="text-xs text-neutral-400">{suffix}</span>}
      </div>
    </div>
  );
}

function OTToggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
          checked ? 'bg-primary-600' : 'bg-neutral-200'
        }`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`} />
      </button>
      <span className="text-xs text-neutral-600">{label}</span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface UserProfileEditProps {
  /**
   * When set, the component shows only this employee's payroll editor (used by
   * the unified Staff Profile page, which provides its own picker). Omit to keep
   * the original full-list behaviour used by the /hr inline section.
   */
  userId?: number;
}

function UserProfileEdit({ userId }: UserProfileEditProps = {}) {
  const embedded = userId != null;
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>({
    first_name: '',
    last_name: '',
    myob_card_id: '',
    xero_employee_code: '',
    pay_frequency: 'fortnightly',
    state: '',
  });
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getUsers();
      if (response.success && response.data) {
        setUsers(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (user: UserData) => {
    setEditingId(user.id);
    setForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      myob_card_id: user.myob_card_id || '',
      xero_employee_code: user.xero_employee_code || '',
      pay_frequency: user.pay_frequency || 'fortnightly',
      state: user.state || '',
    });
    setError(null);
    setSavedId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setError(null);
  };

  // Embedded mode: auto-open the editor for the selected employee.
  useEffect(() => {
    if (userId == null) return;
    const u = users.find(x => x.id === userId);
    if (u) startEdit(u);
    else setEditingId(null);
  }, [userId, users]);

  const handleSave = async (userId: number) => {
    try {
      setSaving(true);
      setError(null);
      const response = await userService.updateUserProfile(userId, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        myob_card_id: form.myob_card_id.trim(),
        xero_employee_code: form.xero_employee_code.trim(),
        pay_frequency: form.pay_frequency,
        state: form.state,
      });
      if (response.success) {
        setUsers(prev =>
          prev.map(u =>
            u.id === userId
              ? {
                  ...u,
                  first_name: form.first_name.trim() || null,
                  last_name: form.last_name.trim() || null,
                  myob_card_id: form.myob_card_id.trim() || null,
                  xero_employee_code: form.xero_employee_code.trim() || null,
                  pay_frequency: (form.pay_frequency as 'weekly' | 'fortnightly') || null,
                  state: (form.state || null) as UserData['state'],
                }
              : u
          )
        );
        setEditingId(null);
        setSavedId(userId);
        setTimeout(() => setSavedId(null), 2500);
      } else {
        setError('Failed to save. Please try again.');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8">
          <p className="text-center text-neutral-600">Loading users...</p>
        </CardContent>
      </Card>
    );
  }

  const visibleUsers = embedded ? users.filter(u => u.id === userId) : users;

  return (
    <Card>
      {!embedded && (
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <UserCog className="h-5 w-5 text-blue-600" />
            </div>
            Payroll Details
            <span className="text-sm font-normal text-neutral-500 ml-1">
              — set legal names, MYOB/Xero IDs, pay frequency, and overtime overrides per employee
            </span>
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className={embedded ? 'pt-6' : undefined}>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-3">
          {visibleUsers.length === 0 && (
            <p className="text-center text-neutral-600 py-4">
              {embedded ? 'Employee not found in your organisation.' : 'No users found in your organisation.'}
            </p>
          )}

          {visibleUsers.map(user => {
            const isEditing = editingId === user.id;
            const wasSaved = savedId === user.id;
            const hasName = user.first_name || user.last_name;
            const freq = user.pay_frequency || 'fortnightly';

            return (
              <div
                key={user.id}
                className="border border-neutral-200 rounded-lg p-4 hover:border-blue-200 transition-colors"
              >
                {isEditing ? (
                  /* ── Edit mode ── */
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <p className="font-semibold text-neutral-900">{user.username}</p>
                      <p className="text-sm text-neutral-500 break-all">{user.email}</p>
                    </div>

                    {/* Row 1: Names */}
                    <div className="flex flex-col sm:flex-row gap-3 mb-3">
                      <div className="flex-1">
                        <label className="block text-xs text-neutral-500 mb-1">First Name</label>
                        <input
                          type="text"
                          value={form.first_name}
                          onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                          placeholder="e.g. Jane"
                          className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          autoFocus
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs text-neutral-500 mb-1">Last Name</label>
                        <input
                          type="text"
                          value={form.last_name}
                          onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                          placeholder="e.g. Smith"
                          className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    {/* Row 2: MYOB Card ID + Xero Employee Code */}
                    <div className="flex flex-col sm:flex-row gap-3 mb-3">
                      <div className="flex-1">
                        <label className="block text-xs text-neutral-500 mb-1">MYOB Employee Card ID</label>
                        <input
                          type="text"
                          value={form.myob_card_id}
                          onChange={e => setForm(f => ({ ...f, myob_card_id: e.target.value }))}
                          placeholder="e.g. EMP001"
                          className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      {user.xero_linked_employee_id ? (
                        // Matched on Accounts → Xero: pay goes to that employee by
                        // the connection, so there is no code to type.
                        <div className="flex-1 min-w-0">
                          <p className="block text-xs text-neutral-500 mb-1">Xero employee</p>
                          <p className="px-3 py-2 border border-green-200 bg-green-50 rounded-lg text-sm text-green-800 break-words">
                            Matched to {user.xero_linked_employee_name || 'a Xero employee'}
                          </p>
                          <p className="mt-1 text-xs text-neutral-500">Changed on Accounts → Xero, Match employees.</p>
                        </div>
                      ) : (
                        <div className="flex-1">
                          <label className="block text-xs text-neutral-500 mb-1">Xero Employee Code</label>
                          <input
                            type="text"
                            value={form.xero_employee_code}
                            onChange={e => setForm(f => ({ ...f, xero_employee_code: e.target.value }))}
                            placeholder="e.g. EMP001"
                            className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      )}
                    </div>

                    {/* Row 3: Pay Frequency + State */}
                    <div className="flex flex-col sm:flex-row gap-3 mb-3">
                      <div className="flex-1">
                        <label className="block text-xs text-neutral-500 mb-1">Pay Frequency</label>
                        <select
                          value={form.pay_frequency}
                          onChange={e => setForm(f => ({ ...f, pay_frequency: e.target.value }))}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="fortnightly">Fortnightly</option>
                          <option value="weekly">Weekly</option>
                        </select>
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs text-neutral-500 mb-1">State (public holidays)</label>
                        <select
                          value={form.state}
                          onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Use org default (national only)</option>
                          {AUSTRALIAN_STATES.map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 mb-3">
                      <Button
                        size="sm"
                        onClick={() => handleSave(user.id)}
                        disabled={saving}
                        className="flex items-center gap-1"
                      >
                        <Check className="h-4 w-4" />
                        {saving ? 'Saving…' : 'Save'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={cancelEdit}
                        disabled={saving}
                        className="flex items-center gap-1"
                      >
                        <X className="h-4 w-4" />
                        Cancel
                      </Button>
                    </div>

                    {/* Overtime override — lazy-loaded when expanded */}
                    <UserOvertimePanel userId={user.id} />
                  </div>
                ) : (
                  /* ── View mode ── */
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <p className="font-semibold text-neutral-900">{user.username}</p>
                        <AdminEmailEditor
                          userId={user.id}
                          email={user.email}
                          onChanged={(email) => setUsers(prev => prev.map(u => (u.id === user.id ? { ...u, email } : u)))}
                        />
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm">
                        <span>
                          {hasName ? (
                            <span className="text-neutral-700">
                              {[user.first_name, user.last_name].filter(Boolean).join(' ')}
                            </span>
                          ) : (
                            <span className="text-neutral-400 italic">No legal name set</span>
                          )}
                        </span>
                        {user.myob_card_id && (
                          <span className="text-neutral-500">
                            MYOB: <span className="font-mono text-neutral-700">{user.myob_card_id}</span>
                          </span>
                        )}
                        {user.xero_linked_employee_id ? (
                          <span className="text-neutral-500 break-words">
                            Xero: <span className="text-neutral-700">{user.xero_linked_employee_name || 'matched'}</span>
                          </span>
                        ) : user.xero_employee_code && (
                          <span className="text-neutral-500">
                            Xero: <span className="font-mono text-neutral-700">{user.xero_employee_code}</span>
                          </span>
                        )}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          freq === 'weekly'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {FREQ_LABEL[freq] ?? freq}
                        </span>
                        {user.state && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                            {user.state}
                          </span>
                        )}
                        {wasSaved && (
                          <span className="text-green-600 text-xs font-medium">✓ Saved</span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => startEdit(user)}
                      className="flex items-center gap-1 shrink-0 ml-3"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default UserProfileEdit;
